'use client';

import { useCallback, useEffect, useRef } from 'react';
import { SchedulerProTriggerProps } from './types';

declare global {
  interface Window {
    _scheduler?: {
      show: (options: { schedulerId: string }) => void;
    };
  }
}

const INITIAL_HASH_DELAY = 1500;
const MAX_RETRIES = 15;
const RETRY_DELAY = 150;

const SCHEDULER_IFRAME_TITLE = 'Book an Appointment';
const SCHEDULER_IFRAME_SELECTOR = '#se-widget-iframe';

const applyIframeTitle = () => {
  const iframes = document.querySelectorAll<HTMLIFrameElement>(SCHEDULER_IFRAME_SELECTOR);
  iframes.forEach((iframe) => {
    if (iframe.getAttribute('title') !== SCHEDULER_IFRAME_TITLE) {
      iframe.setAttribute('title', SCHEDULER_IFRAME_TITLE);
    }
  });
};

export default function SchedulerProTrigger({ schedulerId }: SchedulerProTriggerProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const openScheduler = useCallback(() => {
    if (!schedulerId) return;

    // Try to open the scheduler immediately, then retry if it's not ready
    const tryOpen = (attempt = 0) => {
      if (window._scheduler?.show) {
        window._scheduler.show({ schedulerId });
        return;
      }

      if (attempt < MAX_RETRIES) {
        window.setTimeout(() => tryOpen(attempt + 1), RETRY_DELAY);
      }
    };

    tryOpen();
  }, [schedulerId]);

  const triggerHiddenButton = useCallback(() => {
    buttonRef.current?.click();
  }, []);

  // Watch dom and inject accessible title.
  useEffect(() => {
    if (!schedulerId) return;

    applyIframeTitle();

    const observer = new MutationObserver(() => {
      applyIframeTitle();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [schedulerId]);

  // Handle direct page visits with #schedule
  useEffect(() => {
    if (!schedulerId) return;

    const handleInitialHash = () => {
      if (window.location.hash !== '#schedule') return;

      window.setTimeout(() => {
        triggerHiddenButton();
      }, INITIAL_HASH_DELAY);
    };

    if (document.readyState === 'complete') {
      handleInitialHash();
      return;
    }

    window.addEventListener('load', handleInitialHash);

    return () => {
      window.removeEventListener('load', handleInitialHash);
    };
  }, [schedulerId, triggerHiddenButton]);

  // Handle clicks and later hash changes
  useEffect(() => {
    if (!schedulerId) return;

    const handleHashChange = () => {
      if (window.location.hash === '#schedule') {
        triggerHiddenButton();
      }
    };

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest(
        'a[href="#schedule"], a[href="/#schedule"]',
      ) as HTMLAnchorElement | null;

      if (!anchor) return;

      event.preventDefault();
      triggerHiddenButton();
    };
    

    window.addEventListener('hashchange', handleHashChange);
    document.addEventListener('click', handleDocumentClick, { capture: true });

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      document.removeEventListener('click', handleDocumentClick, { capture: true });
    };
  }, [schedulerId, triggerHiddenButton]);

  if (!schedulerId) return null;

  return (
    <button
      ref={buttonRef}
      type='button'
      className='stsp-data-btn hidden'
      onClick={openScheduler}
      aria-hidden='true'
      tabIndex={-1}
    >
      Book Now!
    </button>
  );
}
